import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Shield } from 'lucide-react';

const STORAGE_KEY = 'cookieConsent';

const cookieText = {
  es: {
    title: 'Uso de cookies',
    body: 'Usamos cookies propias para mejorar tu experiencia y recordar tus preferencias. Puedes aceptarlas o rechazarlas.',
    cookiePolicy: 'Política de Cookies',
    accept: 'Aceptar',
    reject: 'Rechazar',
  },
  en: {
    title: 'Cookie Usage',
    body: 'We use our own cookies to improve your experience and remember your preferences. You can accept or reject them.',
    cookiePolicy: 'Cookie Policy',
    accept: 'Accept',
    reject: 'Reject',
  },
  de: {
    title: 'Cookie-Nutzung',
    body: 'Wir verwenden eigene Cookies, um Ihre Erfahrung zu verbessern und Ihre Einstellungen zu speichern. Sie konnen diese akzeptieren oder ablehnen.',
    cookiePolicy: 'Cookie-Richtlinie',
    accept: 'Akzeptieren',
    reject: 'Ablehnen',
  },
};

export default function CookieConsent() {
  const { lang } = useLanguage();
  const [visible, setVisible] = useState(false);
  const ct = cookieText[lang];

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // runs only on mount to check initial consent

  // Expose a global method so Footer can re-open the banner
  useEffect(() => {
    window.__showCookieBanner = () => setVisible(true);
    return () => { delete window.__showCookieBanner; };
  }, []);

  const handleConsent = useCallback((accepted) => {
    localStorage.setItem(STORAGE_KEY, accepted ? 'accepted' : 'rejected');
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] p-4 sm:p-6 bg-[#121b33]/98 backdrop-blur-xl border-t border-white/10 animate-fade-in" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-5 w-5 text-[#1a6cf5] shrink-0" />
          <h3 className="text-white font-semibold text-sm">{ct.title}</h3>
        </div>
        <p className="text-xs sm:text-sm text-gray-400 leading-relaxed mb-4">
          {ct.body}{' '}
          <Link to="/cookies" className="text-[#1a6cf5] hover:underline">{ct.cookiePolicy}</Link>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => handleConsent(true)}
            className="bg-[#1a6cf5] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#3b82f6] transition-all"
            data-testid="cookie-accept-btn"
          >
            {ct.accept}
          </button>
          <button
            onClick={() => handleConsent(false)}
            className="bg-[#2d3748] text-gray-300 rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#3d4a5c] border border-white/10 transition-all"
            data-testid="cookie-reject-btn"
          >
            {ct.reject}
          </button>
        </div>
      </div>
    </div>
  );
}
